import { useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import styled from 'styled-components';

/**
 * Поле-список «чипами»: значение добавляется по Enter, запятой или точке с
 * запятой, при вставке текст делится по тем же разделителям. Дубли без учёта
 * регистра не добавляются. Backspace в пустом поле удаляет последний чип.
 *
 * С `dragGroup` чипы перетаскиваются мышью: внутри поля — меняют порядок,
 * в другое поле той же группы — переносятся (из исходного убираются).
 */

/** Перетаскиваемый чип: одно перетаскивание за раз на странице. */
interface ChipDrag {
  group: string;
  source: symbol;
  index: number;
  value: string;
  /** Чип принят другим полем — исходное поле уберёт его по окончании. */
  moved: boolean;
}
let drag: ChipDrag | null = null;

const Box = styled.div<{ $disabled?: boolean; $over?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  min-height: 40px;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme, $over }) => ($over ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $over }) =>
    $over ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  cursor: text;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Chip = styled.span<{ $draggable?: boolean; $dragging?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  max-width: 100%;
  padding: 2px 4px 2px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  line-height: 1.5;
  overflow-wrap: anywhere;
  cursor: ${({ $draggable }) => ($draggable ? 'grab' : 'default')};
  opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
`;

const Remove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Entry = styled.input`
  flex: 1 1 140px;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
  padding: ${({ theme }) => theme.spacing(1)} 0;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const SPLIT = /[,;\n]/;

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Максимум элементов (дальше ввод блокируется). */
  maxItems?: number;
  /** Максимальная длина элемента в символах. */
  maxLength?: number;
  id?: string;
  'aria-label'?: string;
  /**
   * Группа перетаскивания: чипы можно перетащить мышью в другое поле с той же
   * группой (перенос) или внутри поля (порядок).
   */
  dragGroup?: string;
}

export function ChipsInput({
  value,
  onChange,
  placeholder,
  disabled,
  maxItems,
  maxLength,
  id,
  'aria-label': ariaLabel,
  dragGroup,
}: Props) {
  const [text, setText] = useState('');
  const [over, setOver] = useState(false);
  const [dragging, setDragging] = useState<number | null>(null);
  const me = useRef(Symbol('chips')).current;
  const full = maxItems != null && value.length >= maxItems;
  const canDrag = !!dragGroup && !disabled;

  /** Можно ли бросить сюда текущий перетаскиваемый чип. */
  function accepts(): boolean {
    if (!canDrag || !drag || drag.group !== dragGroup) return false;
    if (drag.source === me) return true;
    const exists = value.some((v) => v.toLowerCase() === drag?.value.toLowerCase());
    return exists || !full;
  }

  /** Бросили чип перед позицией `pos` (`value.length` — в конец). */
  function dropAt(pos: number) {
    const d = drag;
    if (!d || !accepts()) return;
    if (d.source === me) {
      const to = pos > d.index ? pos - 1 : pos;
      if (to === d.index) return;
      const next = [...value];
      const [item] = next.splice(d.index, 1);
      next.splice(to, 0, item);
      onChange(next);
      return;
    }
    if (!value.some((v) => v.toLowerCase() === d.value.toLowerCase())) {
      const next = [...value];
      next.splice(pos, 0, d.value);
      onChange(next);
    }
    d.moved = true;
  }

  function onDragOverAt(e: DragEvent) {
    if (!accepts()) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOver(true);
  }

  function add(raw: string[]) {
    const seen = new Set(value.map((v) => v.toLowerCase()));
    const next = [...value];
    for (const r of raw) {
      const v = (maxLength ? r.trim().slice(0, maxLength) : r.trim()).trim();
      if (!v || seen.has(v.toLowerCase())) continue;
      if (maxItems != null && next.length >= maxItems) break;
      seen.add(v.toLowerCase());
      next.push(v);
    }
    if (next.length !== value.length) onChange(next);
  }

  function commit() {
    if (!text.trim()) return;
    add(text.split(SPLIT));
    setText('');
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !text && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text');
    if (!SPLIT.test(pasted)) return;
    e.preventDefault();
    add((text + pasted).split(SPLIT));
    setText('');
  }

  return (
    <Box
      $disabled={disabled}
      $over={over}
      onDragOver={onDragOverAt}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        dropAt(value.length);
      }}
    >
      {value.map((v, i) => (
        <Chip
          key={`${v}-${i}`}
          $draggable={canDrag}
          $dragging={dragging === i}
          draggable={canDrag}
          title={canDrag ? 'Перетащите, чтобы перенести или поменять порядок' : undefined}
          onDragStart={(e) => {
            if (!canDrag || !dragGroup) return;
            drag = { group: dragGroup, source: me, index: i, value: v, moved: false };
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', v);
            setDragging(i);
          }}
          onDragEnd={() => {
            const d = drag;
            drag = null;
            setDragging(null);
            if (d && d.source === me && d.moved) onChange(value.filter((_, j) => j !== d.index));
          }}
          onDragOver={(e) => {
            e.stopPropagation();
            onDragOverAt(e);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOver(false);
            dropAt(i);
          }}
        >
          {v}
          {!disabled && (
            <Remove
              type="button"
              aria-label={`Убрать «${v}»`}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              ×
            </Remove>
          )}
        </Chip>
      ))}
      {!disabled && !full && (
        <Entry
          id={id}
          aria-label={ariaLabel}
          value={text}
          placeholder={value.length === 0 ? placeholder : undefined}
          maxLength={maxLength}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={commit}
        />
      )}
    </Box>
  );
}
